import { apiClient } from '@/utils/api'

export interface TTSResult {
  /** DashScope 音频 URL（HTTPS，24h 有效，公开可读），前端 <audio> 顺序播放 */
  urls: string[]
  voice: string
}

export interface TTSPref {
  voice: string
  /** 是否自动预合成每条回复（消耗泡泡，默认关） */
  autoPrefetch: boolean
}

/** 合成结果：成功返回 urls；失败区分 quota(泡泡不足) / rate_limited(限流) / empty(清洗后全符号段) / failed(其他) */
export type SynthResult = TTSResult | { error: 'quota' | 'rate_limited' | 'empty' | 'failed' }

/**
 * 语音播放（TTS）服务。
 *
 * 后端 /api/aie/tts 调 DashScope qwen3-tts-flash，返回音频 URL（音频直连阿里云 OSS，
 * 不经服务器字节流）。长文本由后端分段，前端按 urls 顺序播放。
 * 计费：每次合成扣泡泡（500泡泡/百字）；复听（后端缓存命中）免费。
 */
class TTSService {
  /** timeoutMs：默认 90s（1-2 次 DashScope 调用）；整篇/长块合成可放宽（如故事页 180s） */
  async synthesize(text: string, voice: string, timeoutMs = 90_000): Promise<SynthResult> {
    try {
      // synthChain 全局串行，一个挂死的请求会卡死整条合成链，超时必须兜住
      const res = await apiClient.post<TTSResult>('/aie/tts', { text, voice }, { timeout: timeoutMs })
      return { urls: res.urls, voice: res.voice }
    } catch (e) {
      // apiClient 把 response 挂在 Error 上（见 utils/api.ts），优先按 status 分类；
      // 超时/网络错误无 response → 落入消息文本兜底
      const status = (e as Error & { response?: { status?: number } })?.response?.status
      if (status === 402) {
        return { error: 'quota' }
      }
      if (status === 429) {
        return { error: 'rate_limited' }
      }
      if (status === 400) {
        return { error: 'empty' } // 清洗后空/缺文本——段直接跳过
      }
      const msg = e instanceof Error ? e.message : String(e ?? '')
      // 兜底匹配新文案「泡泡不足」，兼容旧文案「星火不足」（2026-08 更名），并认 QUOTA_EXHAUSTED 错误码
      const code = (e as Error & { response?: { data?: { code?: string } } })?.response?.data?.code
      const isQuota = code === 'QUOTA_EXHAUSTED' || /泡泡不足|星火不足|QUOTA_EXHAUSTED/.test(msg)
      return { error: isQuota ? 'quota' : 'failed' }
    }
  }

  async getPreference(): Promise<TTSPref> {
    try {
      const res = await apiClient.get<TTSPref>('/aie/tts/preference')
      return { voice: res.voice || 'Cherry', autoPrefetch: !!res.autoPrefetch }
    } catch {
      return { voice: 'Cherry', autoPrefetch: false }
    }
  }

  /** 部分更新偏好（voice 和/或 autoPrefetch） */
  async savePreference(patch: Partial<TTSPref>): Promise<TTSPref> {
    const res = await apiClient.put<TTSPref>('/aie/tts/preference', patch)
    return { voice: res.voice || 'Cherry', autoPrefetch: !!res.autoPrefetch }
  }
}

export const ttsService = new TTSService()

/** 平台开放的音色（必须与后端 aieTTS.js 的 ALLOWED_VOICES 一致） */
export const TTS_VOICES: { id: string; name: string; gender: '女' | '男' }[] = [
  { id: 'Cherry', name: '芊悦', gender: '女' },
  { id: 'Serena', name: '苏瑶', gender: '女' },
  { id: 'Maia', name: '四月', gender: '女' },
  { id: 'Chelsie', name: '千雪', gender: '女' },
  { id: 'Ethan', name: '晨煦', gender: '男' },
  { id: 'Eldric Sage', name: '沧明子', gender: '男' },
  { id: 'Neil', name: '阿闻', gender: '男' },
  { id: 'Kai', name: '凯', gender: '男' },
]
